import { Injectable, inject } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Store } from '@ngxs/store';
import { BankDetailsState } from '@wilson/bank-details';
import {
  FetchGeneralCompanySettingsIfNeeded,
  GeneralCompanySettingsState,
} from '@wilson/general-company-settings';
import {
  BankDetails,
  BankDetailsForm,
  GeneralCompanySettings,
} from '@wilson/interfaces';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class GeneralSettingsHelperService {
  private fb = inject(FormBuilder);
  private store = inject(Store);

  createGeneralSettingsForm(): FormGroup {
    return new FormGroup({
      id: this.fb.control(''),
      companyLogo: this.fb.control('', []),
      infoDetails: this.fb.group({
        email: this.fb.control('', [Validators.email]),
        websiteUrl: this.fb.control('', []),
        phone: this.fb.control('', []),
      }),
      addressDetails: this.fb.group({
        street: this.fb.control('', [Validators.required]),
        city: this.fb.control('', [Validators.required]),
        postCode: this.fb.control('', [Validators.required]),
        country: this.fb.control('', [Validators.required]),
        state: this.fb.control('', [Validators.required]),
      }),
      vatDetails: this.fb.group({
        vatId: this.fb.control('', []),
        taxIdentificationNumber: this.fb.control('', []),
        generalManager: this.fb.control('', []),
        localCourt: this.fb.control('', []),
        tradeRegisterNumber: this.fb.control('', []),
      }),
    });
  }

  createBankDetailsFormArray(
    bankDetails: BankDetails[],
    hasPermissions: boolean,
  ): FormArray {
    const formArray = this.fb.array<FormGroup<BankDetailsForm>>([]);
    if (bankDetails.length === 0) {
      formArray.push(this.createBankDetailsFormGroup());
    } else {
      bankDetails.forEach((detail) =>
        formArray.push(this.createBankDetailsFormGroup(detail)),
      );
    }
    this.toggleBankDetailsFormControlsBasedOnPermissions(
      formArray,
      hasPermissions,
    );
    return formArray;
  }

  private toggleBankDetailsFormControlsBasedOnPermissions(
    formArray: FormArray<FormGroup<BankDetailsForm>>,
    hasPermissions: boolean,
  ): void {
    if (hasPermissions) {
      formArray.controls.forEach((formGroup: FormGroup) => {
        formGroup.enable();
      });
    } else {
      formArray.controls.forEach((formGroup: FormGroup) => {
        formGroup.disable();
      });
    }
  }

  createBankDetailsFormGroup(bankDetail?: BankDetails): FormGroup {
    return new FormGroup({
      id: this.fb.control(bankDetail?.id || null),
      bankName: this.fb.control(bankDetail?.bankName || null, [
        Validators.required,
      ]),
      iban: this.fb.control(bankDetail?.iban || null, [Validators.required]),
      bic: this.fb.control(bankDetail?.bic || null, [Validators.required]),
      bankCode: this.fb.control(bankDetail?.bankCode || null),
      accountNumber: this.fb.control(bankDetail?.accountNumber || null),
      accountHolderName: this.fb.control(bankDetail?.accountHolderName || null),
      isDefault: this.fb.control(bankDetail?.isDefault || false),
    });
  }

  getGeneralCompanySettings$(): Observable<GeneralCompanySettings> {
    this.store.dispatch(new FetchGeneralCompanySettingsIfNeeded());
    return this.store.select(
      GeneralCompanySettingsState.generalCompanySettings,
    );
  }

  getBankDetails$(): Observable<BankDetails[]> {
    return this.store.select(BankDetailsState.bankDetails);
  }

  setEmptyValuesToNull(formGroup: FormGroup | FormArray): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);

      if (control instanceof FormControl && control.value === '') {
        control.setValue(null, { emitEvent: false });
      } else if (control instanceof FormGroup || control instanceof FormArray) {
        this.setEmptyValuesToNull(control);
      }
    });
  }

  transformEmptyStringsToNull(obj: Record<string, any>): Record<string, any> {
    const result: Record<string, any> = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        result[key] = obj[key] === '' ? null : obj[key];
      }
    }
    return result;
  }
}
